import type { StatusTone } from "./status";
import type { SessionPushAttempt, SessionPushOutcome } from "./types";

/**
 * The pure half of the push log (`pages/SessionPushes.tsx`): what each answer
 * meant, and the bodies made readable.
 */

const OUTCOME_LABELS: Readonly<Record<SessionPushOutcome, string>> = {
  saved: "saved",
  already_saved: "already saved",
  ignored: "not theirs",
  rejected: "rejected",
  unauthorized: "token refused",
  failed: "failed",
  skipped: "skipped",
  turned_off: "turned off",
};

/** The outcomes a request can have; `skipped` and `turned_off` never send one. */
export const ATTEMPT_OUTCOMES: readonly SessionPushOutcome[] = [
  "saved",
  "already_saved",
  "ignored",
  "rejected",
  "unauthorized",
  "failed",
];

export function attemptOutcomeLabel(outcome: SessionPushOutcome): string {
  return OUTCOME_LABELS[outcome];
}

export function attemptTone(outcome: SessionPushOutcome): StatusTone {
  switch (outcome) {
    case "saved":
    case "already_saved":
      return "available";
    case "ignored":
    case "skipped":
    case "turned_off":
      return "idle";
    default:
      return "faulted";
  }
}

/** "HTTP 422", or why nothing came back. */
export function answerHeadline(attempt: Pick<SessionPushAttempt, "httpStatus" | "error">): string {
  if (attempt.httpStatus !== null) return `HTTP ${attempt.httpStatus}`;
  return attempt.error ?? "no answer";
}

/** JSON indented; anything else exactly as it came. */
export function prettyBody(text: string | null): string {
  if (text === null) return "(nothing came back)";
  if (text === "") return "(an empty body)";
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

export function prettyJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

export function attemptDuration(ms: number): string {
  return ms < 1_000 ? `${ms} ms` : `${(ms / 1_000).toFixed(1)} s`;
}
